import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, Zap, ArrowRight, Sparkles } from 'lucide-react';

import { LoginForm } from '@/components/LoginForm';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

import { useQuery } from 'hooks/query';

import { isAdminConsole } from '@/AppMode';

import { ChainlitContext, useAuth } from 'client-types/*';

export const LoginError = new Error(
  'Error logging in. Please try again later.'
);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  // Carrousel state - 4 slides
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const handleCookieAuth = async (json: any): Promise<void> => {
    if (json?.success != true) throw LoginError;

    await setUserFromAPI();
    
    const needsPasswordChange = await checkFirstLogin();
    if (!needsPasswordChange) {
      navigate('/');
    }
  };

  const checkFirstLogin = async () => {
    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/auth/me'),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const userData = await response.json();
        
        if (userData.first_login === true) {
          console.log('🔐 First login detected, redirecting to change password');
          navigate('/change-password');
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error checking first login:', err);
      return false;
    }
  };

  const handleAuth = async (
    jsonPromise: Promise<any>,
    _redirectURL?: string
  ) => {
    try {
      const json = await jsonPromise;
      await handleCookieAuth(json);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();
    await handleAuth(jsonPromise, '/');
  };

  const handlePasswordLogin = async (email: string, password: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const jsonPromise = apiClient.passwordAuth(formData);
    await handleAuth(jsonPromise);
  };

  const handleCreateTenant = () => {
    navigate('/signup-tenant');
  };

  // Auto-play carrousel - pause au hover
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 7000);

    return () => clearInterval(interval);
  }, [isPaused]);

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (config.headerAuth && !user) {
      handleHeaderAuth();
    }
    if (user) {
      checkFirstLogin().then((needsChange) => {
        if (!needsChange) {
          navigate('/');
        }
      });
    }
  }, [config, user]);

  // Console master : écran neutre, sans marque ni argumentaire (démos, partage d'écran)
  const adminConsole = isAdminConsole();
  useEffect(() => {
    if (!adminConsole) return;
    const previous = document.title;
    document.title = 'Connexion';
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => {
      document.title = previous;
      robots.remove();
    };
  }, [adminConsole]);

  if (adminConsole) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
          <LoginForm
            minimal
            error={error}
            callbackUrl="/admin"
            providers={config?.oauthProviders || []}
            onPasswordSignIn={
              config?.passwordAuth ? handlePasswordLogin : undefined
            }
            onOAuthSignIn={async (provider: string) => {
              window.location.href = apiClient.getOAuthEndpoint(provider);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2 relative">
      {/* LEFT SIDE - Login to Global LegalChat */}
      <div className="flex flex-col min-h-svh bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-950 dark:to-blue-950 relative min-h-svh">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="flex-1 flex flex-col justify-center py-12 px-6 md:px-10 relative z-10 w-full">
          {/* Logo */}
          <div className="w-full flex justify-center mb-8">
            <div className="flex items-center gap-3 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
              <div className="relative">
                <Logo className="w-16 h-16" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">LegalChat</h1>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">by SmartLawyer</p>
                  <span className="inline-flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                    Beta
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form - centré verticalement avec flex-1 */}
          <div className="flex-1 flex items-center justify-center w-full">
            <div className="w-full max-w-md">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8">
                <LoginForm
                  error={error}
                  callbackUrl="/"
                  providers={config?.oauthProviders || []}
                  onPasswordSignIn={
                    config?.passwordAuth ? handlePasswordLogin : undefined
                  }
                  onOAuthSignIn={async (provider: string) => {
                    window.location.href = apiClient.getOAuthEndpoint(provider);
                  }}
                />
              </div>
            </div>
          </div>          
        </div>
      </div>

      {/* SEPARATOR "OU" - avec effet glow */}
      <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
        <div className="relative">
          <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-full w-16 h-16 flex items-center justify-center shadow-2xl border-4 border-gray-100 dark:border-gray-700">
            <span className="text-lg font-bold text-gray-600 dark:text-gray-300">OU</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Create Your LegalChat */}
      {!config?.headerAuth ? (
        <div className="flex flex-col min-h-svh bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 relative overflow-hidden min-h-svh hidden lg:flex">
          {/* Animated background */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="flex-1 flex flex-col justify-center py-8 px-8 md:px-12 relative z-10 text-white w-full max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl mb-4 shadow-2xl">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-2">
                Créez votre LegalChat
              </h2>
              <p className="text-lg text-blue-200">
                Déployez votre propre plateforme en 5 minutes
              </p>
            </div>

            {/* Carrousel Content - avec pause au hover */}
            <div 
              className="flex-1 flex flex-col items-center justify-center w-full"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="w-full relative">
                {/* Slide 1 - Features */}
                <div 
                  className={`transition-all duration-1000 ease-in-out ${
                    currentSlide === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 min-h-[320px]">
                    <h3 className="font-semibold mb-5 text-center text-lg">Fonctionnalités incluses</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-7 h-7 bg-cyan-400/20 rounded-lg flex items-center justify-center">
                          <Check className="w-4 h-4 text-cyan-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-0.5">Instance dédiée pour votre cabinet</h4>
                          <p className="text-xs text-blue-200">Votre propre URL, vos couleurs, votre logo</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-7 h-7 bg-cyan-400/20 rounded-lg flex items-center justify-center">
                          <Check className="w-4 h-4 text-cyan-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-0.5">Agents IA spécialisés inclus</h4>
                          <p className="text-xs text-blue-200">50+ agents juridiques prêts à l'emploi</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-7 h-7 bg-cyan-400/20 rounded-lg flex items-center justify-center">
                          <Check className="w-4 h-4 text-cyan-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-0.5">Données isolées et sécurisées</h4>
                          <p className="text-xs text-blue-200">Conformité RGPD, hébergement France</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-7 h-7 bg-cyan-400/20 rounded-lg flex items-center justify-center">
                          <Check className="w-4 h-4 text-cyan-300" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-0.5">Gestion des utilisateurs intégrée</h4>
                          <p className="text-xs text-blue-200">Invitez votre équipe, gérez les accès</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide 2 - Steps */}
                <div 
                  className={`transition-all duration-1000 ease-in-out ${
                    currentSlide === 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 min-h-[320px]">
                    <h3 className="font-semibold mb-5 text-center text-lg">Configuration en 4 étapes</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-9 h-9 bg-cyan-500 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                        <span className="text-blue-100 text-sm">Informations cabinet (nom, domaine, logo)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-9 h-9 bg-cyan-500 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                        <span className="text-blue-100 text-sm">Personnalisation (couleurs, branding)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-9 h-9 bg-cyan-500 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                        <span className="text-blue-100 text-sm">Sélection des agents IA & paramètres</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-9 h-9 bg-cyan-500 rounded-full flex items-center justify-center text-sm font-bold">4</div>
                        <span className="text-blue-100 text-sm">Ajout / Invitation de vos collaborateurs</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide 3 - Trial + Stats - TAILLES RÉDUITES */}
                <div 
                  className={`transition-all duration-1000 ease-in-out ${
                    currentSlide === 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 min-h-[320px] flex flex-col items-center justify-center">
                    <h3 className="font-semibold mb-5 text-center text-lg">Essai gratuit sans engagement</h3>
                    
                    <div className="bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-xl p-6 mb-6 w-full">
                      <p className="text-center text-blue-100 mb-1 text-xs">Testez gratuitement pendant</p>
                      <p className="text-center text-4xl font-bold text-cyan-300 mb-1">14 jours</p>
                      <p className="text-center text-[10px] text-blue-200">Sans carte bancaire • Sans engagement</p>
                    </div>

                    <div className="grid grid-cols-3 gap-6 w-full">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-cyan-300 mb-1">5 min</div>
                        <div className="text-[10px] text-blue-200">Configuration</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-300 mb-1">50+</div>
                        <div className="text-[10px] text-blue-200">Agents IA</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-300 mb-1">24/7</div>
                        <div className="text-[10px] text-blue-200">Support</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Slide 4 - SmartLawyer Desktop (IA souveraine) */}
                <div
                  className={`transition-all duration-1000 ease-in-out ${
                    currentSlide === 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 min-h-[320px] flex flex-col">
                    <span className="self-center text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-cyan-400/20 text-cyan-200">Nouveau · bêta</span>
                    <h3 className="font-semibold mt-3 mb-4 text-center text-lg">SmartLawyer Desktop</h3>
                    <p className="text-center text-sm text-blue-100 mb-5">L&apos;IA juridique sur votre ordinateur : vos dossiers clients ne quittent jamais votre poste.</p>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 text-cyan-300 flex-shrink-0" /><span className="text-blue-100">Modèle local avec Ollama (Mistral, Qwen)</span></div>
                      <div className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 text-cyan-300 flex-shrink-0" /><span className="text-blue-100">Pièces indexées et interrogées en local</span></div>
                      <div className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 text-cyan-300 flex-shrink-0" /><span className="text-blue-100">1,2 M de décisions via une question anonymisée</span></div>
                    </div>
                    <a href="https://smartlawyer.ai/desktop" target="_blank" rel="noopener noreferrer" className="mt-auto self-center text-sm font-semibold text-cyan-300 hover:text-cyan-200">Découvrir →</a>
                  </div>
                </div>
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-4">
                <button
                  onClick={() => setCurrentSlide(0)}
                  className={`h-2 rounded-full transition-all ${
                    currentSlide === 0 ? 'bg-cyan-400 w-8' : 'bg-white/30 w-2'
                  }`}
                  aria-label="Slide 1"
                />
                <button
                  onClick={() => setCurrentSlide(1)}
                  className={`h-2 rounded-full transition-all ${
                    currentSlide === 1 ? 'bg-cyan-400 w-8' : 'bg-white/30 w-2'
                  }`}
                  aria-label="Slide 2"
                />
                <button
                  onClick={() => setCurrentSlide(2)}
                  className={`h-2 rounded-full transition-all ${
                    currentSlide === 2 ? 'bg-cyan-400 w-8' : 'bg-white/30 w-2'
                  }`}
                  aria-label="Slide 3"
                />
                <button
                  onClick={() => setCurrentSlide(3)}
                  className={`h-2 rounded-full transition-all ${
                    currentSlide === 3 ? 'bg-cyan-400 w-8' : 'bg-white/30 w-2'
                  }`}
                  aria-label="Slide 4"
                />
              </div>
            </div>

            {/* CTA Button + SmartLawyer branding */}
            <div className="w-full mt-12 space-y-6">
              <Button
                onClick={handleCreateTenant}
                className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white py-5 rounded-xl font-bold text-base shadow-2xl hover:shadow-cyan-500/50 transform hover:scale-105 transition-all flex items-center justify-center gap-2 group"
              >
                <Zap className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Démarrer maintenant
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>

              {/* SmartLawyer branding - simplifié */}
              {/* SmartLawyer branding - complet avec copyright et mentions légales */}
              <div className="text-center pt-4 border-t border-white/10 space-y-2">
                <p className="text-sm text-blue-200 flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyan-300" />
                  <span className="font-semibold">SmartLawyer</span>
                  <span className="text-blue-300">•</span>
                  <span>L'IA au service du droit</span>
                </p>
                <p className="text-xs text-blue-300 flex items-center justify-center gap-2">
                  <span>© 2025 SmartLawyer</span>
                  <span className="text-blue-400">•</span>
                  <button className="hover:text-white transition-colors">
                    Mentions légales
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}